import { useRef, useState } from 'react'
import type { AppState, OnboardingStep } from '../../shared/types.ts'
import { ONBOARDING_STEPS } from '../../shared/types.ts'
import { api } from '../lib/api.ts'
import { SCRIPT, scriptFor } from '../lib/script.ts'
import { cx } from '../lib/util.ts'
import { useStickToBottom } from '../lib/useStickToBottom.ts'
import { Bubble, Typing } from './Bubble.tsx'
import { Composer } from './Composer.tsx'

interface Turn { id: string; role: 'user' | 'assistant'; text: string }

let seq = 0
const turn = (role: Turn['role'], text: string): Turn => ({ id: `t${seq++}`, role, text })

const INTRO =
  "Before I can cook for you, I need to know three things: what you can cook with, what you'll eat, and how you feel about shopping. Two minutes, and I won't ask again."

export function Onboarding({ state, onState, onDone, canType }: {
  state: AppState
  onState: (s: AppState) => void
  /** Called when the cook taps through the closing message, not when the data lands. */
  onDone: () => void
  /** Free-text answers need Inception; taps always work without it. */
  canType: boolean
}) {
  const pending = ONBOARDING_STEPS.filter((s) => !state.onboarding.done.includes(s))
  // Captured once: `done` grows as the cook answers, so recomputing this mid-run
  // would make a first-time setup start signing off like a returning one.
  const [resuming] = useState(() => state.onboarding.done.length > 0)
  const [queue, setQueue] = useState<OnboardingStep[]>(pending)
  const [turns, setTurns] = useState<Turn[]>(() => {
    const first = pending[0]
    return [
      ...(resuming ? [] : [turn('assistant', INTRO)]),
      ...(first ? [turn('assistant', scriptFor(first).prompt)] : []),
    ]
  })
  const [picked, setPicked] = useState<string[]>([])
  const [draft, setDraft] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const scrollRef = useRef<HTMLDivElement>(null)
  const dockRef = useRef<HTMLDivElement>(null)

  const step = queue[0]
  const script = step ? scriptFor(step) : null

  useStickToBottom(scrollRef, dockRef, [turns.length, busy, step])

  const submit = async (input: { text?: string; choices?: string[] }, echo: string) => {
    if (!step) return
    setBusy(true)
    setError('')
    setTurns((t) => [...t, turn('user', echo)])
    setPicked([])
    setDraft('')
    try {
      const res = await api.answerStep(step, input)
      onState(res.state)
      const rest = queue.slice(1)
      setQueue(rest)
      const next = rest[0]
      setTurns((t) => [
        ...t,
        turn('assistant', res.summary),
        turn('assistant', next
          ? scriptFor(next).prompt
          : resuming
            ? 'Updated. Back to it.'
            : "That's everything. Tell me what you feel like and I'll go find it."),
      ])
    } catch (e) {
      setError((e as Error).message)
      setTurns((t) => t.slice(0, -1))
    } finally {
      setBusy(false)
    }
  }

  const toggle = (value: string, single: boolean, groupValues: string[]) => {
    if (!script) return
    // Single-choice steps submit on tap, but not while something is half-typed.
    if (script.instant && !draft.trim()) { void submit({ choices: [value] }, labelOf(value)); return }
    setPicked((p) => {
      const without = single ? p.filter((v) => !groupValues.includes(v)) : p
      return without.includes(value) ? without.filter((v) => v !== value) : [...without, value]
    })
  }

  const labelOf = (value: string): string => {
    for (const s of SCRIPT) {
      for (const g of s.groups) {
        const hit = g.options.find((o) => o.value === value)
        if (hit) return hit.label
      }
    }
    return value
  }

  /** Taps and anything typed alongside them go in the same answer. */
  const sendAnswer = () => {
    const text = draft.trim()
    if (!picked.length && !text) return
    const echo = [picked.map(labelOf).join(', '), text].filter(Boolean).join('\n')
    void submit({ choices: picked, text }, echo)
  }

  const done = !step
  const index = ONBOARDING_STEPS.length - queue.length

  return (
    <div className="chat">
      <div className="progress" aria-hidden>
        {ONBOARDING_STEPS.map((s, i) => <span key={s} className={cx('pip', i < index && 'on')} />)}
      </div>

      <div className="messages" ref={scrollRef}>
        {turns.map((t) => (
          <Bubble key={t.id} role={t.role}>{t.text}</Bubble>
        ))}
        {busy && <Typing label="Noting that down" />}
        {error && <Bubble role="assistant" failed>{error}</Bubble>}
      </div>

      <div className="dock" ref={dockRef}>
        {done ? (
          <button className="btn btn-primary btn-block" style={{ padding: 13 }} onClick={onDone}>Start cooking →</button>
        ) : (
          <>
            <div className="chip-scroll">
              {script!.groups.map((g, gi) => (
                <div className="chip-group" key={gi}>
                  {g.label && <span className="chip-group-label">{g.label}</span>}
                  <div className="chips">
                    {g.options.map((o) => (
                      <button
                        key={o.value}
                        type="button"
                        className={cx('pick', picked.includes(o.value) && 'on')}
                        aria-pressed={picked.includes(o.value)}
                        disabled={busy}
                        onClick={() => toggle(o.value, Boolean(g.single), g.options.map((x) => x.value))}
                      >
                        {o.label}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            <div className="dock-actions">
              <Composer
                value={draft}
                onChange={setDraft}
                onSend={sendAnswer}
                placeholder={
                  !canType ? 'Tap the options above'
                    : picked.length ? 'anything to add?'
                    : script!.placeholder
                }
                disabled={!canType}
                busy={busy}
                canSend={picked.length > 0}
              />
            </div>

            {picked.length > 0 && (
              <button className="btn btn-primary btn-block" style={{ padding: 12 }} onClick={sendAnswer} disabled={busy}>
                {draft.trim()
                  ? `Add ${picked.length} and your note →`
                  : `Add ${picked.length} and continue →`}
              </button>
            )}
            {script!.skipLabel && !picked.length && !draft.trim() && (
              <button className="btn btn-ghost" disabled={busy} onClick={() => submit({ choices: [] }, script!.skipLabel!)}>
                {script!.skipLabel}
              </button>
            )}
            <p className="dock-hint">{script!.hint}</p>
          </>
        )}
      </div>
    </div>
  )
}
